<template>
  <v-list color="primary" density="compact" nav :prepend-gap="16">
    <v-list-item
      ref="supportButton"
      v-tooltip:end="{ text: t('support.button'), disabled: !smallNav }"
      base-color="warn"
      prepend-icon="mdi-message-question-outline"
      :title="t('support.button')"
      variant="tonal"
      data-testid="sidemenu-support"
      @click="supportDialog = true"
    />
    <v-list-item
      v-tooltip:end="{ text: t('sidemenu.myAccount') }"
      :to="{ name: 'MyAccount' }"
      prepend-icon="mdi-account-circle-outline"
      data-testid="sidemenu-user"
      :title="user.authenticatedUser?.firebaseUser.email ?? undefined"
    />
    <v-list-item
      v-if="smallNav"
      v-tooltip:end="{ text: t('sidemenu.expand') }"
      prepend-icon="mdi-arrow-expand-right"
      :title="t('sidemenu.expand')"
      @click="smallNav = !smallNav"
    />
  </v-list>
  <v-dialog v-model="supportDialog" width="560" persistent aria-labelledby="support-dialog-title">
    <v-card>
      <v-form ref="supportForm" :disabled="supportLoading" @submit.prevent="sendSupportMessage">
        <v-card-title id="support-dialog-title">{{ t("support.title") }}</v-card-title>
        <v-card-text class="flex flex-col gap-4 pb-0">
          <p>{{ t("support.description") }}</p>
          <v-textarea
            v-model="supportMessage"
            :label="t('support.message')"
            :rules="[required]"
            auto-grow
            autofocus
            counter="5000"
            maxlength="5000"
            rows="5"
            variant="outlined"
          />
          <p class="text-sm text-medium-emphasis -mt-3">
            {{ t("support.preferEmail") }}
            <a :href="`mailto:${SUPPORT_EMAIL}`">{{ SUPPORT_EMAIL }}</a>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn :disabled="supportLoading" @click="supportDialog = false">{{
            t("common.close")
          }}</v-btn>
          <v-btn
            type="submit"
            color="primary"
            :loading="supportLoading"
            :disabled="supportLoading"
            prepend-icon="mdi-send"
            >{{ t("common.send") }}</v-btn
          >
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
  <v-list v-if="smallNav" nav>
    <v-list-item href="https://aircap.tech" target="_blank" class="px-1 pt-2">
      <AircapLogo size="30px" />
    </v-list-item>
  </v-list>
  <v-list v-else nav>
    <v-list-item class="px-1 pt-2">
      <div class="flex items-center">
        <a href="https://aircap.tech" target="_blank" class="flex shrink-0">
          <AircapLogotype size="30px" />
        </a>
        <div class="ml-auto flex items-center gap-4">
          <v-btn
            v-tooltip:top="{ text: t('sidemenu.theme') }"
            color="grey"
            density="compact"
            icon
            size="small"
            variant="text"
            @click="toggleTheme"
          >
            <v-icon :icon="themeIcon" />
          </v-btn>
          <v-menu location="end">
            <template #activator="{ props: languageProps }">
              <v-btn
                v-tooltip:top="{ text: t('sidemenu.language') }"
                v-bind="languageProps"
                color="grey"
                density="compact"
                icon
                size="small"
                variant="text"
              >
                <v-icon icon="mdi-web" />
              </v-btn>
            </template>
            <v-list density="compact">
              <v-list-item
                v-for="lang in languages"
                :key="lang.code"
                :active="currentLanguage.code === lang.code"
                :title="lang.name"
                @click="changeLanguage(lang.code)"
              />
            </v-list>
          </v-menu>
          <v-btn
            v-tooltip:top="{ text: t('sidemenu.collapse') }"
            color="grey"
            density="compact"
            icon
            size="small"
            variant="text"
            @click="smallNav = !smallNav"
          >
            <v-icon icon="mdi-arrow-collapse-left" />
          </v-btn>
        </div>
      </div>
    </v-list-item>
  </v-list>
</template>

<script lang="ts" setup>
import AircapLogo from "@/components/AircapLogo.vue";
import AircapLogotype from "@/components/AircapLogotype.vue";
import { useI18n } from "@/composables/useI18n";
import { useLanguage } from "@/composables/useLanguage";
import { useRules } from "@/composables/useRules";
import { useThemePreference } from "@/composables/useThemePreference";
import { call } from "@/firebase";
import { useToast } from "@/store";
import { useUserStore } from "@/store/user";
import { SUPPORT_EMAIL } from "@aircap/common/config";
import { captureException } from "@sentry/vue";
import { useStorage } from "@vueuse/core";
import { nextTick, ref, watch } from "vue";
import { useRoute } from "vue-router";
import type { VForm } from "vuetify/components";

const { t } = useI18n();
const { required } = useRules();
const route = useRoute();
const user = useUserStore();
const { showSuccessToast, showErrorToast } = useToast();
const { languages, changeLanguage, currentLanguage } = useLanguage();
const { icon: themeIcon, toggleTheme } = useThemePreference();
const smallNav = useStorage("use-small-nav", false);

const supportDialog = ref(false);
const supportButton = ref<{ $el?: HTMLElement }>();
const supportForm = ref<VForm>();
const supportMessage = ref("");
const supportLoading = ref(false);

watch(supportDialog, (open) => {
  if (open) return;
  void nextTick(() => supportButton.value?.$el?.focus());
});

const sendSupportMessage = async () => {
  const validation = await supportForm.value?.validate();
  if (!validation?.valid) return;
  supportLoading.value = true;
  try {
    await call("sendSupportMessage")({ message: supportMessage.value, path: route.fullPath });
    supportDialog.value = false;
    supportMessage.value = "";
    showSuccessToast(t("support.sent"));
  } catch (cause) {
    const error = new Error("Error sending support message", { cause });
    console.error(error);
    captureException(error);
    showErrorToast();
  } finally {
    supportLoading.value = false;
  }
};
</script>
